<template>
  <div class="p-8">
    <!-- Form View -->
    <div v-if="!submitted" class="max-w-2xl mx-auto">
      <h1 class="text-2xl font-bold mb-6">Create Config Unlock Request</h1>

      <div class="bg-white dark:bg-surface-800 rounded-lg shadow p-6">
        <div class="flex flex-col gap-6">
          <FormGroup label="Serial Number" :error-text="errors.serial_number">
            <InputText
              v-model="form.serial_number"
              placeholder="Enter device serial number"
              class="w-full"
            />
          </FormGroup>

          <FormGroup label="Part Number" :error-text="errors.part_number">
            <InputText
              v-model="form.part_number"
              placeholder="Enter part number"
              class="w-full"
            />
          </FormGroup>

          <FormGroup label="Title" :error-text="errors.title">
            <InputText
              v-model="form.title"
              placeholder="Enter request title"
              class="w-full"
            />
          </FormGroup>

          <FormGroup label="Comment" :error-text="errors.comment">
            <Textarea
              v-model="form.comment"
              placeholder="Enter comment"
              rows="3"
              class="w-full"
            />
          </FormGroup>

          <FormGroup label="Seed (Base64)" :error-text="errors.seed">
            <InputText
              v-model="form.seed"
              placeholder="Enter seed in base64 format"
              class="w-full font-mono"
            />
          </FormGroup>

          <FormGroup label="Datetime" :error-text="errors.datetime">
            <InputText
              v-model="form.datetime"
              placeholder="YYYY-MM-DDTHH:MM:SS"
              class="w-full font-mono"
            />
          </FormGroup>

          <FormGroup label="Checksum" :error-text="errors.checksum">
            <InputText
              v-model="form.checksum"
              placeholder="Enter checksum (optional)"
              class="w-full"
            />
          </FormGroup>

          <div class="flex justify-end gap-3 mt-4">
            <Button
              label="Clear Form"
              severity="secondary"
              @click="clearForm"
            />
            <Button
              :label="submitting ? 'Creating...' : 'Create Request'"
              :disabled="submitting"
              @click="submitForm"
            />
          </div>

          <Message v-if="errorMessage" severity="error">
            {{ errorMessage }}
          </Message>
        </div>
      </div>
    </div>

    <!-- Success View -->
    <div v-else class="max-w-3xl mx-auto">
      <h1 class="text-2xl font-bold mb-6">Config Unlock Request Created</h1>

      <div class="bg-white dark:bg-surface-800 rounded-lg shadow p-6 mb-6">
        <div class="flex items-center gap-3 mb-6">
          <i class="pi pi-check-circle text-green-500 text-3xl"></i>
          <span class="text-lg font-medium">Request created successfully!</span>
        </div>

        <div class="space-y-6">
          <!-- Hash Section -->
          <div>
            <div class="flex justify-between items-center mb-2">
              <h2 class="text-lg font-semibold">Hash (Hex)</h2>
              <Button
                label="Copy"
                icon="pi pi-copy"
                size="small"
                text
                @click="copyToClipboard(result.hash, 'Hash')"
              />
            </div>
            <div class="bg-surface-100 dark:bg-surface-700 rounded p-4">
              <code class="font-mono text-sm break-all select-all">{{ result.hash }}</code>
            </div>
            <p class="text-sm text-surface-500 mt-1">{{ result.hash?.length || 0 }} characters ({{ (result.hash?.length || 0) / 2 }} bytes)</p>
          </div>

          <!-- Signature Section -->
          <div>
            <div class="flex justify-between items-center mb-2">
              <h2 class="text-lg font-semibold">Signature (Hex)</h2>
              <Button
                label="Copy"
                icon="pi pi-copy"
                size="small"
                text
                @click="copyToClipboard(result.signature, 'Signature')"
              />
            </div>
            <div class="bg-surface-100 dark:bg-surface-700 rounded p-4">
              <code class="font-mono text-sm break-all select-all">{{ result.signature }}</code>
            </div>
            <p class="text-sm text-surface-500 mt-1">{{ result.signature?.length || 0 }} characters ({{ (result.signature?.length || 0) / 2 }} bytes)</p>
          </div>

          <!-- Signature Bytes - Large Display -->
          <div class="border-2 border-primary-500 rounded-lg p-6 bg-primary-50 dark:bg-primary-900/20">
            <div class="flex justify-between items-center mb-3">
              <h2 class="text-xl font-bold text-primary-700 dark:text-primary-300">Signature Length</h2>
            </div>
            <div class="text-center">
              <span class="text-6xl font-bold text-primary-600 dark:text-primary-400">{{ (result.signature?.length || 0) / 2 }}</span>
              <span class="text-2xl text-primary-500 dark:text-primary-400 ml-2">bytes</span>
            </div>
          </div>

          <!-- Request Details -->
          <div class="border-t border-surface-200 dark:border-surface-600 pt-6">
            <h2 class="text-lg font-semibold mb-3">Request Details</h2>
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span class="text-surface-500">Request ID:</span>
                <span class="ml-2 font-medium">{{ result.id }}</span>
              </div>
              <div>
                <span class="text-surface-500">Serial Number:</span>
                <span class="ml-2 font-medium">{{ result.serial_number }}</span>
              </div>
              <div>
                <span class="text-surface-500">Part Number:</span>
                <span class="ml-2 font-medium">{{ result.part_number }}</span>
              </div>
              <div>
                <span class="text-surface-500">Datetime:</span>
                <span class="ml-2 font-medium">{{ result.datetime }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-end gap-3">
        <Button
          label="Create Another"
          severity="secondary"
          @click="resetForm"
        />
      </div>
    </div>
  </div>
</template>

<script>
import FormGroup from '@/Components/FormGroup.vue';

const LOCAL_STORAGE_KEY = 'configUnlockCreateForm';

export default {
  name: 'ConfigUnlockCreate',
  components: {
    FormGroup,
  },
  data() {
    return {
      form: {
        serial_number: '',
        part_number: '',
        title: '',
        comment: '',
        seed: '',
        datetime: '',
        checksum: '',
      },
      errors: {},
      submitting: false,
      submitted: false,
      errorMessage: null,
      result: null,
    };
  },
  watch: {
    form: {
      handler(newVal) {
        this.saveToLocalStorage();
      },
      deep: true,
    },
  },
  mounted() {
    this.loadFromLocalStorage();
  },
  methods: {
    loadFromLocalStorage() {
      try {
        const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          this.form = { ...this.form, ...parsed };
        }
      } catch (e) {
        console.error('Failed to load form from localStorage:', e);
      }
    },
    saveToLocalStorage() {
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(this.form));
      } catch (e) {
        console.error('Failed to save form to localStorage:', e);
      }
    },
    clearForm() {
      this.form = {
        serial_number: '',
        part_number: '',
        title: '',
        comment: '',
        seed: '',
        datetime: '',
        checksum: '',
      };
      this.errors = {};
      this.errorMessage = null;
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    },
    resetForm() {
      this.submitted = false;
      this.result = null;
      this.errors = {};
      this.errorMessage = null;
    },
    async submitForm() {
      this.errors = {};
      this.errorMessage = null;
      this.submitting = true;

      try {
        const response = await axios.post('config-unlock/create', this.form);
        this.result = response.data;
        this.submitted = true;
      } catch (error) {
        if (error.response?.status === 422) {
          this.errors = error.response.data.errors || {};
          this.errorMessage = 'Validation failed. Please check the form.';
        } else {
          this.errorMessage = error.response?.data?.message || 'Failed to create config unlock request.';
        }
      } finally {
        this.submitting = false;
      }
    },
    copyToClipboard(text, label) {
      navigator.clipboard.writeText(text).then(() => {
        this.$toast.add({
          severity: 'success',
          summary: 'Copied',
          detail: `${label} copied to clipboard`,
          life: 2000,
        });
      }).catch(() => {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to copy to clipboard',
          life: 2000,
        });
      });
    },
  },
};
</script>
